import { ADMIN_SCOPES, hasAdminScope } from '@/lib/auth';

/**
 * Sidebar navigation, resolved for one signed-in person.
 *
 * Navigation is grouped by what the person is trying to *do*, not by which
 * database table backs the page. Each role's most-used destination stays at the
 * top level; everything else folds into a labelled group so the sidebar reads
 * as a short list rather than a wall of links.
 *
 * Scope rule for admins: groups deliberately carry no `scope` of their own.
 * A group-level scope would hide an entire section from a scoped admin even
 * when they are entitled to some of its children, so every child inside a
 * group names its own scope and empty groups are dropped afterwards.
 */

import {
  LayoutDashboard,
  BookOpen,
  Video,
  FileText,
  ClipboardList,
  GraduationCap,
  Languages,
  Users,
  Megaphone,
  Sparkles,
  BarChart3,
  Calendar,
  Pen,
  UserPlus,
  User,
  Inbox,
  Library,
  Building2,
  WalletCards,
  ReceiptText,
  UserCog,
  FileCheck2,
  LifeBuoy,
  Link2,
  MessagesSquare,
  CalendarCheck,
  MessageCircle,
  CalendarDays,
  Scale,
  ShoppingBag,
  PackageSearch,
  Truck,
  RefreshCw,
  Boxes,
  Mail,
  Workflow,
  Globe,
  Folder,
  Star,
  Flag,
  Compass,
  Settings,
} from 'lucide-react';

export const navigationIcons = { LayoutDashboard, BookOpen, Video, FileText, ClipboardList,
  GraduationCap, Languages, Users, Megaphone, Sparkles, BarChart3, Calendar, Pen,
  UserPlus, User, Inbox, Library, Building2, WalletCards, ReceiptText, UserCog,
  FileCheck2, LifeBuoy, Link2, MessagesSquare, CalendarCheck, MessageCircle,
  CalendarDays, Scale, ShoppingBag, PackageSearch, Truck, RefreshCw, Boxes, Mail,
  Workflow, Globe, Folder, Star, Flag, Compass, Settings };

export const studentNav = [
  { label: 'Inbox', path: '/student/inbox', icon: Inbox },
  { label: 'Dashboard', path: '/student/dashboard', icon: LayoutDashboard },
  // Enrolled courses come first: registration is registrar-controlled, so the
  // catalog is a reference list rather than the way into the work. This owns
  // /student/courses so /student/courses/:id keeps "My Courses" highlighted.
  // Coursework is not a separate destination any more: assignments, exams and
  // live classes live inside the course they belong to, which is the only
  // place a student ever needs to look.
  { label: 'My Courses', path: '/student/courses', icon: BookOpen },
  {
    label: 'My Record',
    icon: GraduationCap,
    children: [
      { label: 'My Progress', path: '/student/progress', icon: BarChart3 },
      { label: 'Academic Record', path: '/student/academic-record', icon: FileCheck2 },
      { label: 'Finances', path: '/student/finances', icon: WalletCards },
    ],
  },
  {
    label: 'Services & Orders',
    icon: ShoppingBag,
    children: [
      { label: 'Browse Services', path: '/student/services', icon: ShoppingBag },
      { label: 'My Orders', path: '/student/orders', icon: PackageSearch },
    ],
  },
  { label: 'Course Catalog', path: '/student/catalog', icon: Library },
  { label: 'My Profile', path: '/student/profile', icon: User },
];

export const adminNav = [
  { label: 'Dashboard', path: '/admin/dashboard', icon: LayoutDashboard, scope: ADMIN_SCOPES.ADMIN },
  {
    label: 'Academic',
    icon: Library,
    children: [
      { label: 'Courses', path: '/admin/courses', icon: BookOpen, scope: ADMIN_SCOPES.ACADEMIC },
      { label: 'Programs', path: '/admin/programs', icon: Library, scope: ADMIN_SCOPES.ACADEMIC },
      { label: 'Course Intakes', path: '/admin/course-intakes', icon: GraduationCap, scope: ADMIN_SCOPES.ACADEMIC },
      { label: 'Assignments', path: '/admin/assignments', icon: FileText, scope: ADMIN_SCOPES.ACADEMIC },
      { label: 'Exams', path: '/admin/exams', icon: ClipboardList, scope: ADMIN_SCOPES.ACADEMIC },
      { label: 'Live Sessions', path: '/admin/live-sessions', icon: Calendar, scope: ADMIN_SCOPES.ACADEMIC },
      { label: 'Submissions', path: '/admin/submissions', icon: Pen, scope: ADMIN_SCOPES.ACADEMIC },
      { label: 'Grades', path: '/admin/grades', icon: GraduationCap, scope: ADMIN_SCOPES.ACADEMIC },
      // Course communication belongs with the rest of the academic tools.
      { label: 'Announcements', path: '/admin/announcements', icon: Megaphone, scope: ADMIN_SCOPES.ACADEMIC },
    ],
  },
  {
    label: 'People',
    icon: Users,
    children: [
      { label: 'Students', path: '/admin/students', icon: Users, scope: ADMIN_SCOPES.STUDENT },
      { label: 'Admissions', path: '/admin/applications', icon: Inbox, scope: ADMIN_SCOPES.STUDENT },
      { label: 'Agencies', path: '/admin/agencies', icon: Building2, scope: ADMIN_SCOPES.STUDENT },
      { label: 'Email Templates', path: '/admin/email-templates', icon: Mail, scope: ADMIN_SCOPES.STUDENT },
      // Was filed under Academic, but adding a person is a people task.
      { label: 'Add Instructor', path: '/admin/instructors/new', icon: UserPlus, scope: ADMIN_SCOPES.ACADEMIC },
      { label: 'Accounts', path: '/admin/accounts', icon: UserCog, scope: ADMIN_SCOPES.ADMIN },
    ],
  },
  {
    label: 'Finance',
    icon: WalletCards,
    children: [
      { label: 'Orders', path: '/admin/document-services/orders', icon: PackageSearch, scope: ADMIN_SCOPES.ADMIN },
      { label: 'Products & Services', path: '/admin/document-services/products', icon: Boxes, scope: ADMIN_SCOPES.ADMIN },
      { label: 'Payments', path: '/admin/document-services/payments', icon: ReceiptText, scope: ADMIN_SCOPES.ADMIN },
      { label: 'Fulfillment', path: '/admin/document-services/fulfillment', icon: Truck, scope: ADMIN_SCOPES.ADMIN },
      { label: 'Reconciliation', path: '/admin/document-services/reconciliation', icon: RefreshCw, scope: ADMIN_SCOPES.ADMIN },
      // Accounting is a section, not a page: Sales (invoices, customers,
      // catalog) and Payments (bills, catalog). The sidebar supports one level
      // of children, so the entry lands on the section default and the two
      // groups are laid out by the section's own navigation band.
      { label: 'Accounting', path: '/admin/accounting', icon: WalletCards, scope: ADMIN_SCOPES.ADMIN },
      { label: 'Commissions', path: '/admin/commissions', icon: WalletCards, scope: ADMIN_SCOPES.ADMIN },
    ],
  },
  {
    label: 'Insights',
    icon: BarChart3,
    children: [
      { label: 'Analytics', path: '/admin/analytics', icon: BarChart3, scope: ADMIN_SCOPES.ADMIN },
      { label: 'Reports', path: '/admin/reports', icon: FileText, scope: ADMIN_SCOPES.ADMIN },
      { label: 'AI Marketing', path: '/admin/marketing', icon: Sparkles, scope: ADMIN_SCOPES.ADMIN },
      { label: 'Automations', path: '/admin/automations', icon: Workflow, scope: ADMIN_SCOPES.ADMIN },
    ],
  },
  // Everything the public website is fed from, grouped together: the events
  // themselves, the legal text registrations are evidence against, and the two
  // inboxes visitors land in.
  {
    label: 'Events',
    icon: CalendarDays,
    children: [
      { label: 'All Events', path: '/admin/events', icon: CalendarDays, scope: ADMIN_SCOPES.STUDENT },
      { label: 'Legal & Policies', path: '/admin/legal-policies', icon: Scale, scope: ADMIN_SCOPES.STUDENT },
    ],
  },
  // Language tests are their own section rather than a card on a profile:
  // they are sent to students, to applicants, and to people who are neither
  // yet, so no one record type owns them.
  { label: 'Language Tests', path: '/admin/language-tests', icon: Languages, scope: ADMIN_SCOPES.STUDENT },
  { label: 'Inbox', path: '/admin/inbox', icon: Inbox },
  // Everything the public website sends us, in one place: chat conversations,
  // contact-form messages, newsletter opt-ins and event registrations. They
  // are four separate record types, so this is a group rather than one page —
  // and it stays out of Team Chat above, which is internal.
  //
  // Overview lives at /overview rather than at the group root so it highlights
  // only on its own page: a parent path would light up for every child.
  //
  // Online Chat Messages and Event Signups keep their original URLs — the
  // labels changed, the addresses did not. Every link
  // ever sent, every notification already in someone's inbox and every
  // bookmark still lands on the right page; the area's own tab band is what
  // makes them feel like one section.
  {
    label: 'Website Engagement',
    icon: Globe,
    children: [
      { label: 'Overview', path: '/admin/website-engagement/overview', icon: LayoutDashboard, scope: ADMIN_SCOPES.STUDENT },
      { label: 'Online Chat Messages', path: '/admin/website-messages', icon: MessageCircle, scope: ADMIN_SCOPES.STUDENT },
      { label: 'Contact Form Inquiries', path: '/admin/website-engagement/contact-inquiries', icon: Inbox, scope: ADMIN_SCOPES.STUDENT },
      { label: 'Newsletter Subscribers', path: '/admin/website-engagement/newsletter', icon: Mail, scope: ADMIN_SCOPES.STUDENT },
      { label: 'Event Signups', path: '/admin/website-signups', icon: CalendarCheck, scope: ADMIN_SCOPES.STUDENT },
    ],
  },
  { label: 'My Profile', path: '/admin/profile', icon: User },
];

export const instructorNav = [
  { label: 'Inbox', path: '/instructor/inbox', icon: Inbox },
  { label: 'Dashboard', path: '/instructor/dashboard', icon: LayoutDashboard },
  // Teaching happens inside a course, not beside it. Content, materials,
  // assignments, exams, live classes, the roster and the gradebook are all
  // surfaces of the course room, so the nav only has to get you to a course.
  { label: 'My Courses', path: '/instructor/courses', icon: BookOpen },
  { label: 'Announcements', path: '/instructor/announcements', icon: Megaphone },
  { label: 'My Profile', path: '/instructor/profile', icon: User },
];

export const agencyNav = [
  { label: 'Inbox', path: '/agency/inbox', icon: Inbox },
  { label: 'Dashboard', path: '/agency/dashboard', icon: LayoutDashboard },
  { label: 'Referrals', path: '/agency/referrals', icon: Inbox },
  { label: 'Event Links', path: '/agency/event-links', icon: Link2 },
  { label: 'Students', path: '/agency/students', icon: Users },
  { label: 'Commissions', path: '/agency/commissions', icon: ReceiptText },
  { label: 'Documents', path: '/agency/documents', icon: FileCheck2 },
  { label: 'My Profile', path: '/agency/profile', icon: Building2 },
  { label: 'Support', path: '/agency/support', icon: LifeBuoy },
];

const NAV_BY_ROLE = {
  admin: adminNav,
  instructor: instructorNav,
  agency: agencyNav,
  student: studentNav,
};

/**
 * Resolve the nav for a role, applying admin scope filtering.
 * Non-admin roles have no scopes, so their nav is returned as-is.
 */
export function buildNavItems(role, profile, layout = null) {
  const nav = role === 'admin' && layout ? resolveAdminNavigation(layout) : NAV_BY_ROLE[role];
  if (!nav) return [];
  if (role !== 'admin') return nav;

  return nav
    .filter((item) => !item.scope || hasAdminScope(profile, item.scope))
    .map((item) => {
      if (!item.children) return item;
      return {
        ...item,
        children: item.children.filter(
          (child) => !child.scope || hasAdminScope(profile, child.scope)
        ),
      };
    })
    .filter((item) => !item.children || item.children.length > 0);
}

const iconName = (icon) => Object.keys(navigationIcons).find((key) => navigationIcons[key] === icon) || 'Folder';
const groupId = (label) => `group-${label.toLowerCase().replace(/\s+/g, '-')}`;
export const navigationNodeId = (node) => node.type === 'group' ? node.id : node.path;

export function defaultNavigationLayout() {
  const link = (item) => ({ type: 'link', path: item.path, label: item.label, icon: iconName(item.icon) });
  return adminNav.map((item) => item.children
    ? { type: 'group', id: groupId(item.label), label: item.label, icon: iconName(item.icon), children: item.children.map(link) }
    : link(item));
}

// Routes and permissions always come from the application registry. Saved
// presentation cannot introduce a URL, change access, or lose a newly added page.
export function normalizeNavigationLayout(saved) {
  const defaults = defaultNavigationLayout();
  if (!Array.isArray(saved)) return defaults;
  const catalog = new Map(defaults.flatMap((item) => item.children || [item]).map((item) => [item.path, item]));
  const seen = new Set();
  const groups = new Set();
  const presentation = (item, fallback) => ({
    label: typeof item.label === 'string' && item.label.trim() ? item.label.trim().slice(0, 60) : fallback.label,
    icon: Object.prototype.hasOwnProperty.call(navigationIcons, item.icon) ? item.icon : fallback.icon,
  });
  const link = (item) => {
    if (item?.path === '/admin/chat') item = { ...item, path: '/admin/inbox', label: item.label === 'Team Chat' ? 'Inbox' : item.label };
    if (!item || !catalog.has(item.path) || seen.has(item.path)) return null;
    seen.add(item.path);
    const original = catalog.get(item.path);
    return { ...original, ...presentation(item, original) };
  };
  const result = [];
  saved.slice(0, 100).forEach((item) => {
    if (!item || typeof item !== 'object') return;
    if (item.type === 'group') {
      if (typeof item.id !== 'string' || !/^[a-z][a-z0-9-]{0,79}$/.test(item.id) || groups.has(item.id)) return;
      groups.add(item.id);
      result.push({ type: 'group', id: item.id, ...presentation(item, { label: 'New topic', icon: 'Folder' }),
        children: (Array.isArray(item.children) ? item.children : []).map(link).filter(Boolean) });
    } else {
      const clean = link(item);
      if (clean) result.push(clean);
    }
  });
  defaults.forEach((item) => {
    if (!item.children) {
      if (!seen.has(item.path)) result.push(link(item));
      return;
    }
    const missing = item.children.filter((child) => !seen.has(child.path)).map(link);
    if (!missing.length) return;
    const existing = result.find((entry) => entry.type === 'group' && entry.id === item.id);
    if (existing) existing.children.push(...missing);
    else result.push({ ...item, children: missing });
  });
  return result;
}

function resolveAdminNavigation(layout) {
  const catalog = new Map(adminNav.flatMap((item) => item.children || [item]).map((item) => [item.path, item]));
  const link = (item) => ({ ...catalog.get(item.path), label: item.label, icon: navigationIcons[item.icon] });
  return normalizeNavigationLayout(layout).map((item) => item.type === 'group'
    ? { id: item.id, label: item.label, icon: navigationIcons[item.icon], children: item.children.map(link) }
    : link(item));
}

export function moveNavigationNode(items, id, parentId = null, beforeId = null) {
  const next = items.map((item) => item.children ? { ...item, children: [...item.children] } : { ...item });
  const source = next.find((item) => navigationNodeId(item) === id)
    || next.flatMap((item) => item.children || []).find((item) => navigationNodeId(item) === id);
  if (!source || id === beforeId || (source.type === 'group' && parentId)) return items;
  const destination = parentId ? next.find((item) => item.type === 'group' && item.id === parentId)?.children : next;
  if (!destination) return items;
  const lists = [next, ...next.filter((item) => item.children).map((item) => item.children)];
  lists.forEach((list) => { const index = list.findIndex((item) => navigationNodeId(item) === id); if (index >= 0) list.splice(index, 1); });
  const index = beforeId ? destination.findIndex((item) => navigationNodeId(item) === beforeId) : -1;
  destination.splice(index < 0 ? destination.length : index, 0, source);
  return next;
}

/**
 * Whether a nav path should render as the active route.
 * A path matches its own page and anything nested beneath it, so
 * /student/courses/abc keeps "My Courses" highlighted.
 */
export function isNavPathActive(path, pathname) {
  if (!path || !pathname) return false;
  if (pathname === path) return true;
  if (path === '/') return false;
  return pathname.startsWith(`${path}/`);
}
